"use client"

import * as React from "react"
import { Check, ChevronDown, Loader } from "react-feather"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface ComboboxOption {
  value: string
  label: string
  disabled?: boolean
}

export interface ComboboxGroupOption {
  label: string
  options: ComboboxOption[]
}

export interface ComboboxProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  options?: ComboboxOption[]
  groups?: ComboboxGroupOption[]
  disabled?: boolean
  className?: string
  /** サーバーサイド検索用: 検索値変更時のコールバック */
  onSearchChange?: (value: string) => void
  /** 無限スクロール用: 追加読み込み時のコールバック */
  onLoadMore?: () => void
  /** ローディング状態 */
  isLoading?: boolean
  /** 追加データがあるかどうか */
  hasMore?: boolean
  /** ポップオーバーを開いた時のコールバック（検索状態リセット用） */
  onOpen?: () => void
}

const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      value,
      onChange,
      placeholder = "選択してください",
      searchPlaceholder = "検索...",
      emptyMessage = "見つかりません",
      options,
      groups,
      disabled,
      className,
      onSearchChange,
      onLoadMore,
      isLoading,
      hasMore,
      onOpen,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)
    const [searchValue, setSearchValue] = React.useState("")
    const listRef = React.useRef<HTMLDivElement>(null)

    const allOptions = React.useMemo(() => {
      if (options) return options
      if (groups) return groups.flatMap((group) => group.options)
      return []
    }, [options, groups])

    const selectedOption = allOptions.find((option) => option.value === value)

    const handleSelect = (currentValue: string) => {
      onChange?.(currentValue === value ? "" : currentValue)
      setOpen(false)
    }

    // 検索値変更時のハンドラ（デバウンス付き）
    const handleSearchChange = React.useCallback(
      (value: string) => {
        setSearchValue(value)
        onSearchChange?.(value)
      },
      [onSearchChange]
    )

    // スクロール検知による追加読み込み
    const handleScroll = React.useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        if (!onLoadMore || !hasMore || isLoading) return

        const target = e.target as HTMLDivElement
        const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight

        // 下端から50px以内でトリガー
        if (scrollBottom < 50) {
          onLoadMore()
        }
      },
      [onLoadMore, hasMore, isLoading]
    )

    // ポップオーバーを開いた時に検索値をリセットし、親にも通知
    const handleOpenChange = React.useCallback(
      (isOpen: boolean) => {
        setOpen(isOpen)
        if (isOpen) {
          setSearchValue("")
          onOpen?.()
        }
      },
      [onOpen]
    )

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            aria-expanded={open}
            disabled={disabled}
            className={cn("w-full justify-between font-normal", className)}
          >
            {selectedOption ? selectedOption.label : placeholder}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command shouldFilter={!onSearchChange}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={handleSearchChange}
            />
            <CommandList ref={listRef} onScroll={handleScroll}>
              {!isLoading && allOptions.length === 0 && <CommandEmpty>{emptyMessage}</CommandEmpty>}
              {options && (
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      disabled={option.disabled}
                      onSelect={() => handleSelect(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {groups?.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {group.options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      disabled={option.disabled}
                      onSelect={() => handleSelect(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
              {isLoading && (
                <div className="flex items-center justify-center py-2">
                  <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
)
Combobox.displayName = "Combobox"

export { Combobox }
